// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  type FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { PageContent } from '@/components/page-content';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  CheckCircleOutlineRounded,
  DoneAllRounded,
  ExpandMoreRounded,
  InboxOutlined,
  NotificationsOutlined,
  RadioButtonUncheckedOutlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import { useClickToConfirm } from '@/hooks/use-click-to-confirm';
import { InboxState } from '@/notification/api/inbox';
import { KnownInboxEntry } from '@/notification/api/notification-types';
import { NotificationBody } from '@/notification/components/notification-body';
import { useInbox, useInboxReadState } from '@/notification/hooks/use-inbox';
import {
  toRenderedBody,
  toRenderedText,
} from '@/notification/utilities/render-message';
import { toRelativeTime } from '@/utilities/date-time';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import { MouseEvent, ReactNode, useState } from 'react';

const PAGE_SIZE = 25;
const FIRST_PAGE = 1;

const INBOX_FILTER_OPTIONS: FilterOption<InboxState>[] = [
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
  { value: 'all', label: 'All' },
];

/** Adds the key to the set when absent, and removes it when present. */
function toToggledSet(current: Set<string>, key: string) {
  const next = new Set(current);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
}

export function NotificationInboxPage() {
  const [state, setState] = useState<InboxState>('unread');
  const [page, setPage] = useState(FIRST_PAGE);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const inbox = useInbox({ state, page, limit: PAGE_SIZE });
  const { markRead, markUnread, markAllRead } = useInboxReadState();

  const messages = inbox.data?.results ?? [];
  // What the filter matched, which is not what the page drew: a message this
  // bundle cannot draw is still one the caller has unread
  const totalCount = inbox.data?.totalCount ?? 0;
  const isSaving = [markRead, markUnread, markAllRead].some(
    (mutation) => mutation.isPending
  );
  // A selection survives neither a filter change nor a page turn, so what is
  // ticked is always something the caller can see
  const selectedOnPage = messages.filter((message) =>
    selectedKeys.has(message.key)
  );
  // A selection acts on each message's own state rather than the filter's, so
  // the all filter can hold both and offer both
  const unreadSelection = selectedOnPage.filter(
    (message) => message.readAt === null
  );
  const readSelection = selectedOnPage.filter(
    (message) => message.readAt !== null
  );
  const areAllSelected =
    messages.length > 0 && selectedOnPage.length === messages.length;

  const clearSelection = () => setSelectedKeys(new Set());

  const handleFilterChange = (value: InboxState) => {
    setState(value);
    setPage(FIRST_PAGE);
    setExpandedKeys(new Set());
    clearSelection();
  };

  const handlePageChange = (value: number) => {
    setPage(value);
    clearSelection();
  };

  const handleSelect = (messageKey: string) => {
    setSelectedKeys((current) => toToggledSet(current, messageKey));
  };

  const handleSelectAll = () => {
    setSelectedKeys(
      areAllSelected ? new Set() : new Set(messages.map((one) => one.key))
    );
  };

  const handleMarkSelectionRead = () => {
    markRead.mutate(
      unreadSelection.map((message) => message.key),
      { onSuccess: clearSelection }
    );
  };

  const handleMarkSelectionUnread = () => {
    markUnread.mutate(
      readSelection.map((message) => message.key),
      { onSuccess: clearSelection }
    );
  };

  const handleExpand = (messageKey: string) => {
    setExpandedKeys((current) => toToggledSet(current, messageKey));
  };

  const markAllConfirm = useClickToConfirm(() => {
    // Nothing is left to act on once everything is read, so the ticks go too
    clearSelection();
    markAllRead.mutate();
  });

  return (
    <PageContent>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <NotificationsOutlined fontSize="small" color="action" />
            <Typography variant="h6">Notifications</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              component={RouterLink}
              to="/notifications/subscriptions"
              variant="outlined"
              startIcon={<SettingsOutlined />}
            >
              Manage subscriptions
            </Button>
            <Button
              variant={markAllConfirm.isPending ? 'contained' : 'outlined'}
              startIcon={<DoneAllRounded />}
              // The count is what the state matched, so a page of messages this
              // bundle cannot draw still leaves something to clear
              disabled={state === 'read' || totalCount === 0 || isSaving}
              onClick={markAllConfirm.confirm}
            >
              {markAllConfirm.isPending ? 'Click to confirm' : 'Mark all read'}
            </Button>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <FilterToggleGroup
            value={state}
            options={INBOX_FILTER_OPTIONS}
            onChange={handleFilterChange}
            ariaLabel="Notification read status filter"
          />
          {selectedOnPage.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selectedOnPage.length} selected
              </Typography>
              {unreadSelection.length > 0 && (
                <Button
                  size="small"
                  startIcon={<CheckCircleOutlineRounded />}
                  disabled={isSaving}
                  onClick={handleMarkSelectionRead}
                >
                  Mark as read ({unreadSelection.length})
                </Button>
              )}
              {readSelection.length > 0 && (
                <Button
                  size="small"
                  startIcon={<RadioButtonUncheckedOutlined />}
                  disabled={isSaving}
                  onClick={handleMarkSelectionUnread}
                >
                  Mark as unread ({readSelection.length})
                </Button>
              )}
            </Stack>
          )}
        </Stack>
        {inbox.isError ? (
          <Alert severity="error">Notifications could not be loaded</Alert>
        ) : inbox.isPending ? (
          <Stack alignItems="center" padding={6}>
            <CircularProgress />
          </Stack>
        ) : messages.length === 0 ? (
          <NotificationEmptyState
            state={state}
            hasUndrawableMessages={(inbox.data?.droppedMessageCount ?? 0) > 0}
          />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={areAllSelected}
                      indeterminate={
                        selectedOnPage.length > 0 && !areAllSelected
                      }
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'Select all notifications' }}
                    />
                  </TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell width={160}>Received</TableCell>
                  <TableCell
                    width={144}
                    align="right"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((message) => (
                  <NotificationRow
                    key={message.key}
                    message={message}
                    isExpanded={expandedKeys.has(message.key)}
                    isSelected={selectedKeys.has(message.key)}
                    isSaving={isSaving}
                    onSelect={handleSelect}
                    onExpand={handleExpand}
                    onMarkRead={(messageKey) => markRead.mutate([messageKey])}
                    onMarkUnread={(messageKey) =>
                      markUnread.mutate([messageKey])
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* A filter that matched nothing has no pages to step through */}
        {!inbox.isError && totalCount > 0 && (
          <TablePaginationFooter
            pagination={{
              page,
              pageSize: PAGE_SIZE,
              onPageChange: handlePageChange,
            }}
            totalCount={totalCount}
            entityOptions={{ singular: 'notification', includeCount: false }}
          />
        )}
      </Stack>
    </PageContent>
  );
}

interface NotificationRowProps {
  message: KnownInboxEntry;
  isExpanded: boolean;
  isSelected: boolean;
  isSaving: boolean;
  onSelect: (messageKey: string) => void;
  onExpand: (messageKey: string) => void;
  onMarkRead: (messageKey: string) => void;
  onMarkUnread: (messageKey: string) => void;
}

function NotificationRow(props: NotificationRowProps) {
  const isRead = props.message.readAt !== null;
  const title = toRenderedText(
    props.message.titleTemplate,
    props.message.context
  );
  const body = toRenderedBody(
    props.message.bodyTemplate,
    props.message.context
  );

  const handleReadStateClick = (event: MouseEvent) => {
    // Expanding a message is not reading it, so the row's own click must not run
    event.stopPropagation();
    if (isRead) {
      props.onMarkUnread(props.message.key);
    } else {
      props.onMarkRead(props.message.key);
    }
  };

  const handleSelectClick = (event: MouseEvent) => {
    // Ticking a message is not expanding it, so the row's own click must not run
    event.stopPropagation();
    props.onSelect(props.message.key);
  };

  return (
    <>
      <TableRow
        hover
        selected={props.isSelected}
        onClick={() => props.onExpand(props.message.key)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={props.isSelected}
            onClick={handleSelectClick}
            inputProps={{ 'aria-label': `Select ${title}` }}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* A dot that holds its space whether or not it is drawn, so a read
                row and an unread one line up */}
            <Box
              aria-label={isRead ? undefined : 'Unread'}
              sx={{
                backgroundColor: isRead ? 'transparent' : 'primary.main',
                borderRadius: '50%',
                flexShrink: 0,
                height: 8,
                width: 8,
              }}
            />
            <Stack minWidth={0} flex={1}>
              <Typography variant="body2" fontWeight={isRead ? 400 : 700}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {body?.summary}
              </Typography>
            </Stack>
            <ExpandMoreRounded
              sx={{
                flexShrink: 0,
                transform: props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: (theme) =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
              }}
            />
          </Stack>
        </TableCell>
        <TableCell width={160}>
          <MultiTimezoneTooltip timestamp={props.message.createdAt}>
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
              }}
            >
              {toRelativeTime(props.message.createdAt)}
            </Typography>
          </MultiTimezoneTooltip>
        </TableCell>
        <TableCell width={144} align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Button
            size="small"
            variant="text"
            disabled={props.isSaving}
            onClick={handleReadStateClick}
          >
            {isRead ? 'Mark as unread' : 'Mark as read'}
          </Button>
        </TableCell>
      </TableRow>
      {props.isExpanded && body !== null && (
        <TableRow>
          <TableCell colSpan={4} sx={{ backgroundColor: 'action.hover' }}>
            <Stack spacing={1} paddingY={1} paddingX={7}>
              <NotificationBody
                messageType={props.message.messageType}
                columns={body.columns}
                details={body.details}
              />
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** What an empty inbox shows under the filter that emptied it. */
const EMPTY_STATE_CONTENT: Record<
  InboxState,
  { icon: ReactNode; title: string; detail?: string }
> = {
  unread: {
    icon: <CheckCircleOutlineRounded color="success" fontSize="large" />,
    title: "You're all caught up",
  },
  read: {
    icon: <InboxOutlined color="disabled" fontSize="large" />,
    title: 'No read notifications yet',
    detail: 'Notifications you mark as read will appear here',
  },
  all: {
    icon: <InboxOutlined color="disabled" fontSize="large" />,
    title: 'No notifications yet',
    detail: 'Notifications you receive will appear here',
  },
};

const UNDRAWABLE_STATE_CONTENT = {
  icon: <InboxOutlined color="disabled" fontSize="large" />,
  title: 'Nothing to show on this page',
  detail: 'These notifications could not be displayed',
};

interface NotificationEmptyStateProps {
  state: InboxState;
  hasUndrawableMessages: boolean;
}

function NotificationEmptyState(props: NotificationEmptyStateProps) {
  const content = props.hasUndrawableMessages
    ? UNDRAWABLE_STATE_CONTENT
    : EMPTY_STATE_CONTENT[props.state];

  return (
    <Paper variant="outlined">
      <Stack alignItems="center" spacing={1} padding={6} textAlign="center">
        {content.icon}
        <Typography variant="h6">{content.title}</Typography>
        {content.detail !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {content.detail}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
